'use client';

import type { TerraUIMessage } from '@terra/shared-dto-chat-types';
import {
  extractPendingAskUserInteraction,
  AskUserPromptCard,
  type ChatRef,
} from '@terra/ui-design-system';
import { InterruptedTurnNotice } from './InterruptedTurnNotice';
import { useChatTurnInterruptedStore } from '../../store/chat-turn-interrupted-store';
import { useChatInstancesStore } from '../../store/chatInstancesStore';
import { useCopilotComposerTopContent } from './useCopilotComposerTopContent';
import { useOpenSubagentActivity } from './useOpenSubagentActivity';
import { QueuedMessagesPanel } from './QueuedMessagesPanel';
import { TokenUsagePanel } from './token-usage/TokenUsagePanel';
import type { TokenUsageEstimate } from './token-usage/tokenUsageEstimate';
import type { CompactionActivity } from './token-usage/compactionActivity';
import type { useChatInstance } from '../../hooks/useChatInstance';
import type { useCopilotMainContentNavigation } from './useCopilotMainContentNavigation';
import type { useCopilotChatEventHandlers } from './useCopilotChatEventHandlers';
import type { useCoPilotDevTools } from './dev-tools/useCoPilotDevTools';

export function useCopilotComposerSections({
  chatId,
  messages,
  targetId,
  targetType,
  appTargetId,
  editingQueuedMessageId,
  setEditingQueuedMessageId,
  chatComponentRef,
  instance,
  navigation,
  handlers,
  devToolsEnabled,
  devTools,
  tokenUsageEstimate,
  compactionActivity,
}: {
  chatId: string;
  messages: TerraUIMessage[];
  targetId: string | undefined;
  targetType: string | undefined;
  appTargetId: string | undefined;
  editingQueuedMessageId: string | null;
  setEditingQueuedMessageId: (id: string | null) => void;
  chatComponentRef: React.RefObject<ChatRef | null>;
  instance: ReturnType<typeof useChatInstance>;
  navigation: ReturnType<typeof useCopilotMainContentNavigation>;
  handlers: ReturnType<typeof useCopilotChatEventHandlers>;
  devToolsEnabled: boolean;
  devTools: ReturnType<typeof useCoPilotDevTools>;
  tokenUsageEstimate: TokenUsageEstimate;
  compactionActivity: CompactionActivity;
}) {
  const isTurnInterrupted = useChatTurnInterruptedStore((state) =>
    state.interruptedChatIds.has(chatId),
  );
  const removeQueuedMessage = useChatInstancesStore(
    (state) => state.removeQueuedMessage,
  );
  const reorderQueuedMessages = useChatInstancesStore(
    (state) => state.reorderQueuedMessages,
  );

  const queuedMessagesPanel =
    instance.queuedMessages.length > 0 ? (
      <QueuedMessagesPanel
        queuedMessages={instance.queuedMessages}
        onSendNow={handlers.handleSendQueuedMessageNow}
        onEdit={handlers.handleEditQueuedMessage}
        onReorder={({ fromIndex, toIndex }) =>
          reorderQueuedMessages({ chatId, fromIndex, toIndex })
        }
        onRemove={({ queueItemId }) => {
          if (editingQueuedMessageId === queueItemId) {
            setEditingQueuedMessageId(null);
            chatComponentRef.current?.clearInput();
          }

          removeQueuedMessage({ chatId, queueItemId });
        }}
      />
    ) : undefined;

  const pendingAskUserInteraction = extractPendingAskUserInteraction(messages);
  const branchAskUserDataMessage =
    instance.branchEditSource?.kind === 'ask-user'
      ? instance.branchEditSource.askUserDataMessage
      : null;
  const activeAskUserMessage =
    branchAskUserDataMessage ?? pendingAskUserInteraction?.dataMessage ?? null;

  const openSubagentActivity = useOpenSubagentActivity({
    applicationId: appTargetId,
    openOrFocusTab: navigation.openOrFocusTab,
    revealMainContent: navigation.revealMainContentIfDetachedCopilot,
  });
  const composerTopContent = useCopilotComposerTopContent({
    chatId,
    messages,
    targetId,
    targetType,
    onOpenActivity: openSubagentActivity,
    onOpenObject: navigation.handleOpenChatObject,
    interruptedTurnNotice: isTurnInterrupted ? (
      <InterruptedTurnNotice
        chatId={chatId}
        onContinue={handlers.handleContinueInterruptedTurn}
      />
    ) : undefined,
    tokenUsagePanel:
      devToolsEnabled && devTools.isTokenUsageOpen ? (
        <TokenUsagePanel
          estimate={tokenUsageEstimate}
          compactionActivity={compactionActivity}
          onClose={() => devTools.closeTool('token-usage')}
        />
      ) : undefined,
    askUserPrompt: activeAskUserMessage ? (
      <AskUserPromptCard
        dataMessage={activeAskUserMessage}
        selectedValue={
          branchAskUserDataMessage
            ? undefined
            : pendingAskUserInteraction?.selectedValue
        }
        answerMentions={
          branchAskUserDataMessage
            ? undefined
            : pendingAskUserInteraction?.answerMentions
        }
        answerSegments={
          branchAskUserDataMessage
            ? undefined
            : pendingAskUserInteraction?.answerSegments
        }
        onDataMessageEvent={
          branchAskUserDataMessage
            ? handlers.handleBranchAskUserDataMessageEvent
            : handlers.handleDataMessageEvent
        }
      />
    ) : undefined,
    queuedMessagesPanel,
  });

  return {
    branchAskUserDataMessage,
    composerTopContent,
  };
}
